import type { AddressDocument } from '@util/types/firestore/address';

/**
 * Matches "PO Box", "P.O. Box", "Post Office Box" (case-insensitive) anywhere
 * in address_line1. Shared by shipping-rates.ts (carrier restriction) and the
 * seller address save / make-primary flows (a PO Box can't be a shipping
 * origin or a seller's main/business address). GB #1419.
 */
const PO_BOX_PATTERN = /\b(p\.?\s*o\.?\s*box|post\s*office\s*box)\b/i;

export function isPOBoxAddress(address: { address_line1: string }): boolean {
  return PO_BOX_PATTERN.test(address.address_line1);
}

/**
 * Postal-field shape ShipEngine returns as `matched_address` from
 * /v1/addresses/validate. Only used by validate-address.ts's response
 * contract and the client's AddressValidationResult (user.service.ts) — the
 * make-primary flow (validate-address.ts's sole consumer, see its doc
 * comment) never rewrites address fields, only is_default/is_shop_location.
 */
export interface MatchedAddressFields {
  address_line1?: string | null;
  address_line2?: string | null;
  city_locality?: string | null;
  state_province?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
}

/**
 * Moves is_default (and, when requested, is_shop_location) to the address at
 * `targetIndex`. Always by ARRAY INDEX — never by matching address_line1 —
 * since two addresses can legitimately share the same line1 (e.g. same
 * building, different suite only in line2).
 *
 * Throws on an out-of-bounds index rather than silently producing an array
 * with zero default addresses — callers (AddressCard) must catch this and
 * treat it as a stale-state conflict, not swallow it.
 */
export function moveDefaultAddressByIndex(
  addresses: AddressDocument[],
  targetIndex: number,
  alsoMoveShopLocation: boolean
): AddressDocument[] {
  if (targetIndex < 0 || targetIndex >= addresses.length) {
    throw new Error(
      `moveDefaultAddressByIndex: index ${targetIndex} is out of bounds for ${addresses.length} addresses`
    );
  }
  return addresses.map((addr, i) => {
    const isTarget = i === targetIndex;
    return {
      ...addr,
      is_default: isTarget,
      is_shop_location: alsoMoveShopLocation
        ? isTarget
        : addr.is_shop_location,
    };
  });
}

export type ShipEngineMessageLike = {
  message?: string | null;
  code?: string | null;
  type?: string | null;
};

export type ValidateAddressStatus = 'verified' | 'warning' | 'error';

/**
 * Maps ShipEngine's raw 4-state address-validation status ('verified' |
 * 'unverified' | 'warning' | 'error') onto our 3-state contract.
 * 'unverified' is ShipEngine's "we couldn't confirm it, but nothing looks
 * wrong either" state — we treat it as a soft 'warning' UNLESS a message
 * signals the address is actually undeliverable, in which case it's
 * promoted to a blocking 'error'.
 */
export function mapShipEngineStatus(
  shipEngineStatus: string,
  messages: ShipEngineMessageLike[]
): ValidateAddressStatus {
  if (shipEngineStatus === 'verified') return 'verified';
  if (shipEngineStatus === 'error') return 'error';
  if (shipEngineStatus === 'unverified') {
    const undeliverable = messages.some(
      (m) =>
        m.type === 'error' ||
        /undeliverable/i.test(m.message ?? '') ||
        /undeliverable/i.test(m.code ?? '')
    );
    return undeliverable ? 'error' : 'warning';
  }
  return 'warning';
}
